import React, { useMemo, useState } from 'react';
import { BarChart3, TrendingUp, Users, Clock, Calendar } from 'lucide-react';
import useFleetStats from '../../../hooks/useFleetStats';

const RANGES = [
  { key: '7', label: '7 j', days: 7 },
  { key: '30', label: '30 j', days: 30 },
  { key: '90', label: '90 j', days: 90 }
];

function levelLabel(v) {
  if (v < 0.7) return 'Vide';
  if (v < 1.5) return 'Moyen';
  return 'Blindé';
}

function HourlyChart({ hourly }) {
  const max = 2;
  return (
    <div className="db-hourly-chart">
      {hourly.map((val, h) => {
        const pct = Math.min(1, val / max) * 100;
        const isService = (h >= 11 && h <= 13) || (h >= 18 && h <= 20);
        return (
          <div key={h} className={`db-hourly-bar ${isService ? 'is-service' : ''}`}
            title={`${h}h — ${val.toFixed(2)}`}>
            <div className="db-hourly-fill" style={{ height: `${pct}%` }} />
            <span className="db-hourly-label">{h}</span>
          </div>
        );
      })}
    </div>
  );
}

function CrousRanking({ data, crousMap }) {
  const ranked = useMemo(() => {
    return Object.entries(data)
      .map(([id, rows]) => {
        const totalPings = rows.reduce((a, r) => a + (r.pingCount || 0), 0);
        const wSum = rows.reduce((a, r) => a + (r.avgLevel || 0) * (r.pingCount || 0), 0);
        const avg = totalPings > 0 ? wSum / totalPings : 0;
        const devices = rows.reduce((a, r) => a + (r.uniqueDevices || 0), 0);
        return { id, totalPings, avg, devices, days: rows.length };
      })
      .sort((a, b) => b.totalPings - a.totalPings);
  }, [data]);

  if (ranked.length === 0) return null;

  return (
    <div className="db-ranking">
      <div className="db-ranking-head">
        <span>RU</span>
        <span>Pings</span>
        <span>Moy.</span>
        <span>Devices</span>
      </div>
      {ranked.map((r) => (
        <div key={r.id} className="db-ranking-row">
          <strong>{(crousMap && crousMap[r.id]) || r.id}</strong>
          <span>{r.totalPings.toLocaleString('fr-FR')}</span>
          <span className={`db-level-pill db-level-${r.avg < 0.7 ? 'low' : r.avg < 1.5 ? 'mid' : 'high'}`}>
            {r.avg.toFixed(1)}
          </span>
          <span>{r.devices.toLocaleString('fr-FR')}</span>
        </div>
      ))}
    </div>
  );
}

function StatsTab({ crousIds, crousMap }) {
  const [rangeKey, setRangeKey] = useState('7');
  const range = RANGES.find((r) => r.key === rangeKey);
  const { data, fleet, loading } = useFleetStats(crousIds, range.days);

  if (loading) return <div className="db-empty">Chargement des statistiques…</div>;

  if (fleet.totalDays === 0) {
    return (
      <div className="db-empty">
        <BarChart3 size={28} style={{ marginBottom: 12, color: 'var(--primary)' }} />
        <strong style={{ display: 'block', marginBottom: 6 }}>Pas encore de données agrégées</strong>
        L'agrégation tourne chaque nuit à 00h30 (Paris). Les statistiques apparaîtront
        dès demain avec les premiers signalements.
      </div>
    );
  }

  return (
    <div className="db-stats">
      {/* Range picker */}
      <div className="db-stats-toolbar">
        <Calendar size={14} />
        <div className="db-range-picker">
          {RANGES.map((r) => (
            <button key={r.key} type="button"
              className={`db-chip ${rangeKey === r.key ? 'is-selected' : ''}`}
              onClick={() => setRangeKey(r.key)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="db-stats-kpis">
        <div className="db-stats-kpi">
          <BarChart3 size={18} />
          <div>
            <strong>{fleet.totalPings.toLocaleString('fr-FR')}</strong>
            <span>signalements</span>
          </div>
        </div>
        <div className="db-stats-kpi">
          <TrendingUp size={18} />
          <div>
            <strong>{fleet.avgLevel.toFixed(2)}</strong>
            <span>affluence moy. ({levelLabel(fleet.avgLevel)})</span>
          </div>
        </div>
        <div className="db-stats-kpi">
          <Clock size={18} />
          <div>
            <strong>{fleet.peakHour}h</strong>
            <span>heure de pointe ({fleet.hourly[fleet.peakHour].toFixed(2)})</span>
          </div>
        </div>
        <div className="db-stats-kpi">
          <Users size={18} />
          <div>
            <strong>{fleet.totalDays}</strong>
            <span>jours·RU agrégés</span>
          </div>
        </div>
      </div>

      {/* Hourly heatmap */}
      <div className="db-card">
        <h3>Affluence moyenne par heure</h3>
        <p className="db-muted">
          Moyenne pondérée sur {range.label} pour tous tes RU. Les créneaux de service sont surlignés.
        </p>
        <HourlyChart hourly={fleet.hourly} />
        <div className="db-hint">
          <BarChart3 size={12} /> 0 = vide, 1 = moyen, 2 = blindé
        </div>
      </div>

      {/* Ranking */}
      <div className="db-card">
        <h3>Classement des RU</h3>
        <p className="db-muted">
          Trié par nombre de signalements sur la période.
        </p>
        <CrousRanking data={data} crousMap={crousMap} />
      </div>
    </div>
  );
}

export default StatsTab;
